import { Injectable } from "@angular/core";
import { BaseLookupService } from "../../impl/BaseLookupService";
import { ICustomFieldLookupService } from "../ICustomFieldLookupService";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

const CUSTOM_FIELD_PREFIX: string = "CUSTOM_FIELD_";
const SERVER_PROPERTY_PREFIX: string = "customField";

@Injectable()
export class CustomFieldLookupService
  extends BaseLookupService<string, CubiqTS.CustomFieldImpl>
  implements ICustomFieldLookupService
{
  constructor() {
    super(CubiqTS.CustomFieldImpl);
  }

  getItemKey(item: CubiqTS.CustomFieldImpl): string {
    return item.fieldId;
  }

  getCustomField(fieldName: string): CubiqTS.CustomFieldImpl {
    let item: CubiqTS.CustomFieldImpl = this.myItemMap.get(fieldName);
    if (item) {
      return item;
    }
    return null;
  }

  getAllCustomFields(): CubiqTS.CustomFieldImpl[] {
    return Array.from(this.myItemMap.values());
  }

  /**
   * Get a list of all enabled Custom fields sorted by their ordinal number. Not currently
   * cached - resorted every request
   * @returns
   */
  getActiveCustomFields(): CubiqTS.CustomFieldImpl[] {
    let customFields: CubiqTS.CustomField[] = Array.from(this.myItemMap.values());
    const activeFields = customFields.filter((field) => field.isEnabledFg);
    this.sortCustomFields(activeFields);
    return activeFields;
  }

  /**
   * Get the entity property name used on the server for querying
   * @param defn
   * @returns
   */
  public getEntityPropertyName(defn: CubiqTS.CustomField): string {
    if (defn) {
      const ordinal = this.getCustomFieldOrdinal(defn);
      if (!isNaN(ordinal)) {
        return SERVER_PROPERTY_PREFIX + ordinal;
      }
      return defn.fieldId;
    }
    return null;
  }

  isEnabled(fieldName: string): boolean {
    let item: CubiqTS.CustomFieldImpl = this.myItemMap.get(fieldName);
    if (item) {
      return item.isEnabledFg;
    }
    return false;
  }

  /**
   * Gets the ordinal number for the given custom field based
   * on the custom field ID. Used to position the custom field
   * columns in blotters. Not the best solution as it depends on
   * naming convention but one that is backed by the DB schema.
   * @param field the field to get the ordinal number
   * @returns the ordinal number of the field or null if one cannot be determined.
   */
  getCustomFieldOrdinal(field: CubiqTS.CustomFieldImpl): number {
    let customFieldOrdinal: number = null;

    if (
      field != null &&
      field.fieldId != null &&
      field.fieldId.startsWith(CUSTOM_FIELD_PREFIX) &&
      field.fieldId.length > CUSTOM_FIELD_PREFIX.length
    ) {
      let ordinal: number = Number(field.fieldId.substring(CUSTOM_FIELD_PREFIX.length));
      if (!Number.isNaN(ordinal)) {
        customFieldOrdinal = ordinal;
      }
    }

    return customFieldOrdinal;
  }

  /**
   * Return a new array of CustomFields sorted by their ordinal value
   * @param fields
   * @returns
   */
  private sortCustomFields(fields: CubiqTS.CustomField[]): CubiqTS.CustomField[] {
    const fieldMap = new Map<CubiqTS.CustomField, number>();
    fields.forEach((field) => fieldMap.set(field, this.getCustomFieldOrdinal(field)));

    // Sort based on Ordinal (NaN will map to 0)
    const compareFields = (defn1: CubiqTS.CustomField, defn2: CubiqTS.CustomField): number =>
      defn1 ? (defn2 ? fieldMap.get(defn1) - fieldMap.get(defn2) : 1) : defn2 ? -1 : 0;
    return fields.sort(compareFields);
  }
}
